import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Redirect, type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, Illus404, LoadingAnimation } from '@garnish/core';
import { Image } from '@garnish/media';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getSharedLineItemProductName } from '@order/shared-line-item';
import { useTelemetry } from '@order/Telemetry';

import { useLastInteractedStore } from '../../context';
import { useCustomizeBaseProductForRestaurantQuery } from './graphql/CustomizeBaseProduct.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizeBaseProductScreen = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const lastInteractedStore = useLastInteractedStore();
  const { track } = useTelemetry();
  const isQuickAddEnabled = useFeatureFlag('CELS-5657-order-quick-add-modifiable-product-enabled'); // prettier-ignore

  const { baseProductLineageKey, redirectTo, sharedLineItemId, sharedLineItemSlug } =
    useLocalSearchParams<RouteParams>();

  // ─── Refs ────────────────────────────────────────────────────────────

  const trackedSharedLineItemSlugRef = useRef<string | undefined>(undefined);

  // ─── State ───────────────────────────────────────────────────────────

  // NOTE: Last interacted store can populate after the first render, so wait one tick before
  //       treating a missing location as the real empty state.
  const [hasCheckedInitialLocation, setHasCheckedInitialLocation] = useState(false);

  // ─────────────────────────────────────────────────────────────────────

  const lastInteractedStoreId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [lineageProductResponse] = useCustomizeBaseProductForRestaurantQuery({
    pause: !baseProductLineageKey || !lastInteractedStore?.restaurantId,
    variables: { lineageKey: baseProductLineageKey ?? '', restaurantId: lastInteractedStoreId },
    requestPolicy: 'network-only',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const restaurantId = lastInteractedStore?.restaurantId;
  const deliveryAddressId = lastInteractedStore?.addressId;

  const hasLocation = Boolean(restaurantId);
  const hasResolvedLocation = hasLocation || hasCheckedInitialLocation;
  const productForRestaurant = lineageProductResponse.data?.productForRestaurantByLineageKey;
  const sharedLineItemProductName = getSharedLineItemProductName({ sharedLineItemSlug });

  const isFetchingProduct = lineageProductResponse.fetching || lineageProductResponse.stale;
  const isUnavailableProduct = hasLocation && !isFetchingProduct && (!productForRestaurant || productForRestaurant.outOfStock); // prettier-ignore
  const hasShareRedirect = checkIfShareRedirect(redirectTo);
  const shouldDismissToShare = isUnavailableProduct && hasShareRedirect;
  const isModifiableProduct = productForRestaurant?.isModifiable === true;
  const shouldQuickAddSharedProduct = isQuickAddEnabled && hasShareRedirect && isModifiableProduct;

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocations = useCallback(() => {
    router.dismissTo('/location-selection');
  }, [router]);

  const trackSharedLineItemCustomizationStarted = useCallback(() => {
    if (!sharedLineItemSlug) return;
    if (trackedSharedLineItemSlugRef.current === sharedLineItemSlug) return;
    if (!hasLocation || isUnavailableProduct || !productForRestaurant) return;
    if (shouldQuickAddSharedProduct) return;

    trackedSharedLineItemSlugRef.current = sharedLineItemSlug;

    track('share-line-item.customization-started', {
      isSharedLineItem: true,
      sharedLineItemProductName,
      sharedLineItemSlug,
    });
  }, [
    hasLocation,
    isUnavailableProduct,
    productForRestaurant,
    sharedLineItemProductName,
    sharedLineItemSlug,
    shouldQuickAddSharedProduct,
    track,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (hasCheckedInitialLocation || hasLocation) return;

    setHasCheckedInitialLocation(true);
  }, [hasCheckedInitialLocation, hasLocation]);

  useEffect(() => {
    if (!isUnavailableProduct || !hasShareRedirect) return;

    router.dismissTo(redirectTo);
  }, [isUnavailableProduct, redirectTo, router, hasShareRedirect]);

  useEffect(trackSharedLineItemCustomizationStarted, [trackSharedLineItemCustomizationStarted]);

  // ─────────────────────────────────────────────────────────────────────

  if (!hasResolvedLocation || (hasLocation && isFetchingProduct) || shouldDismissToShare) {
    return <LoadingAnimation backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  // ─── Redirect ────────────────────────────────────────────────────────

  if (hasLocation && !isUnavailableProduct && productForRestaurant) {
    const productSlug = productForRestaurant.slug;
    const restaurantSlug = productForRestaurant.restaurant.slug;

    if (shouldQuickAddSharedProduct) {
      return (
        <Redirect
          href={{
            pathname: '/quick-add/[baseProductLineageKey]',
            params: {
              baseProductLineageKey,
              ...(sharedLineItemId ? { sharedLineItemId } : {}),
            },
          }}
        />
      );
    }

    if (deliveryAddressId) {
      return (
        <Redirect
          href={{
            pathname: '/delivery/[addressId]/[productSlug]',
            params: {
              addressId: deliveryAddressId,
              productSlug,
              ...(sharedLineItemId ? { sharedLineItemId } : {}),
              ...(sharedLineItemSlug ? { sharedLineItemSlug } : {}),
            },
          }}
        />
      );
    }

    return (
      <Redirect
        href={{
          pathname: '/[restaurantSlug]/[productSlug]',
          params: {
            productSlug,
            restaurantSlug,
            ...(sharedLineItemId ? { sharedLineItemId } : {}),
            ...(sharedLineItemSlug ? { sharedLineItemSlug } : {}),
          },
        }}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView style={styles.outerContainer} contentContainerStyle={styles.content}>
      <View style={styles.container}>
        <Image
          source={Illus404}
          alt={formatMessage(messages.illustrationAlt)}
          style={styles.illustration}
          contentFit="contain"
        />

        <DisplayText sizeMatch={['32']} style={styles.title}>
          <FormattedMessage {...messages.title} />
        </DisplayText>

        <BodyText sizeMatch={['18']} style={styles.description}>
          {isUnavailableProduct ? (
            <FormattedMessage {...messages.unavailableProduct} />
          ) : (
            <FormattedMessage {...messages.description} />
          )}
        </BodyText>

        <Button size="large" onPress={navigateToLocations} style={styles.button}>
          <FormattedMessage {...messages.cta} />
        </Button>
      </View>
    </ScrollView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfShareRedirect(redirectTo: string | undefined): redirectTo is ShareRedirect {
  return redirectTo?.startsWith('/share/') === true;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing['20'],
    paddingHorizontal: theme.spacing['6'],
  },
  container: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  illustration: {
    width: 250,
    height: 250,
  },
  title: {
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
  },
  button: {
    marginTop: theme.spacing['10'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  illustrationAlt: {
    defaultMessage: 'An empty bowl illustration',
    description: 'Customize base product | Not found illustration alternative text',
  },
  title: {
    defaultMessage: 'Not available',
    description: 'Customize base product | Not available title',
  },
  description: {
    defaultMessage: 'Select a location to start ordering.',
    description: 'Customize base product | Select location description',
  },
  unavailableProduct: {
    defaultMessage: 'This product is not available at your selected location.',
    description: 'Customize base product | Unavailable product message',
  },
  cta: {
    defaultMessage: 'Change location',
    description: 'Customize base product | Change location CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductLineageKey: string;
  redirectTo?: string;
  sharedLineItemId?: string;
  sharedLineItemSlug?: string;
};

type ShareRedirect = Href & `/share/${string}`;
